import { markProjectBridgeRecommendationsApplied } from "../controller/mark-project-bridge-recommendation-applied"
import { ProjectBridgeRecommendationApplied } from "../schedule-editor-event-definitions.mjs"

/**
 * Collect the persistable identities ({ overlapId, changeId }) of the Project Bridge
 * recommendations within `recommendations`, along with the shared importId. Non-Project-Bridge
 * entries and entries missing an overlap/change/import id are ignored. Duplicates are removed.
 *
 * @param {Array<object>} recommendations
 * @returns {{ importId: string|null, targets: Array<{ overlapId: string, changeId: string }> }}
 */
export function collectProjectBridgeAppliedTargets(recommendations) {
    const seen = new Set()
    const targets = []
    let importId = null

    for (const recommendation of Array.isArray(recommendations) ? recommendations : []) {
        if (recommendation?.sourceType !== "projectBridge") continue

        const overlapId = `${recommendation?.sourceRecordId || ""}`.trim()
        const changeId = `${recommendation?.sourceChangeId || ""}`.trim()
        if (!overlapId || !changeId) continue

        if (!importId && recommendation?.sourceImportId) {
            importId = `${recommendation.sourceImportId}`.trim() || null
        }

        const key = `${overlapId}:${changeId}`
        if (seen.has(key)) continue
        seen.add(key)
        targets.push({ overlapId, changeId })
    }

    return { importId, targets }
}

/**
 * Persist the "applied" flag for all Project Bridge targets in a single server call, then notify
 * the recommendations tab (scoped by draftId) to refetch.
 *
 * Returns the result from the server ({ applied, skipped }) so the caller can record exactly
 * which changes were flagged into the undo snapshot — preventing partial-apply / lost-undo.
 * Returns null when there are no targets, ids are missing, or importId is absent.
 *
 * @param {object} params
 * @param {string|null} params.importId
 * @param {Array<{ overlapId: string, changeId: string }>} params.targets
 * @param {string} params.projectId
 * @param {string} params.scheduleId
 * @param {string} params.draftId
 * @param {boolean} params.applied
 * @returns {Promise<{ importId: string, targets: Array } | null>}
 */
export async function persistProjectBridgeAppliedTargets({
    importId,
    targets,
    projectId,
    scheduleId,
    draftId,
    applied,
}) {
    if (!Array.isArray(targets) || targets.length === 0) return null
    if (!projectId || !scheduleId || !importId) return null

    const result = await markProjectBridgeRecommendationsApplied({
        projectId,
        scheduleId,
        importId,
        targets,
        applied,
    })

    if (draftId) ProjectBridgeRecommendationApplied(draftId).raise()

    // Return the importId + targets so the undo snapshot knows what to reverse.
    return { importId, targets: result?.applied ?? targets }
}
